/**
 * Keyboard Handling Utilities
 * Manages keyboard visibility and layout adjustments for mobile
 */

import { isNativePlatform } from '../config';

let keyboardHeight = 0;
let isKeyboardVisible = false;

/**
 * Get current keyboard height (0 if hidden)
 */
export function getKeyboardHeight(): number {
  return keyboardHeight;
}

/**
 * Check if keyboard is currently visible
 */
export function isKeyboardShowing(): boolean {
  return isKeyboardVisible;
}

/**
 * Set CSS custom property for keyboard height
 */
function updateKeyboardCssVar(height: number): void {
  document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
}

/**
 * Initialize keyboard handling for native platforms
 * Sets up listeners for keyboard show/hide events
 */
export async function initKeyboard(): Promise<void> {
  if (!isNativePlatform()) {
    updateKeyboardCssVar(0);
    return;
  }

  try {
    const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard');

    // Set resize mode to adjust body size when keyboard shows
    await Keyboard.setResizeMode({ mode: KeyboardResize.Body });

    // Show accessory bar (autofill suggestions, email suggestions, etc.)
    await Keyboard.setAccessoryBarVisible({ isVisible: true });

    // Listen for keyboard show
    await Keyboard.addListener('keyboardWillShow', (info) => {
      keyboardHeight = info.keyboardHeight;
      isKeyboardVisible = true;
      updateKeyboardCssVar(keyboardHeight);
      document.body.classList.add('keyboard-visible');
    });

    // Listen for keyboard hide
    await Keyboard.addListener('keyboardWillHide', () => {
      keyboardHeight = 0;
      isKeyboardVisible = false;
      updateKeyboardCssVar(0);
      document.body.classList.remove('keyboard-visible');
    });
  } catch (err) {
    console.warn('Could not initialize keyboard handling:', err);
    updateKeyboardCssVar(0);
  }
}

/**
 * Scroll an element into view above the keyboard
 * Useful for ensuring input fields are visible when keyboard opens
 */
export function scrollIntoViewAboveKeyboard(element: HTMLElement): void {
  if (!isKeyboardVisible) return;

  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight - keyboardHeight;

  // If element is below the visible area above keyboard
  if (rect.bottom > viewportHeight) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

export default {
  getKeyboardHeight,
  isKeyboardShowing,
  initKeyboard,
  scrollIntoViewAboveKeyboard,
};
